import { createSignal, onCleanup, onMount, batch } from 'solid-js';
import { isServer } from 'solid-js/web';
import { ThemeName, SurfaceVariant, sectionColorThemeClasses } from '../../style/color-classes';
import tailwindConfig from '../../../tailwind.config';

const HEADER_HEIGHT = parseFloat(tailwindConfig.theme.extend.height.headerDesktop) * 16; // rem to px
const SCROLL_TOLERANCE = 50; // px buffer for detecting section near top

export function useSectionThemeInHeader() {
    const [headerVariant, setHeaderVariant] = createSignal<SurfaceVariant | null>(null);

    let ticking = false;
    let cachedSections: Element[] = [];

    const cacheSections = () => {
        cachedSections = Array.from(document.querySelectorAll('[data-section-theme]'));
    };

    const updateCurrentSection = () => {
        let foundSection: Element | null = null;

        for (const section of cachedSections) {
            const rect = section.getBoundingClientRect();

            // Check if this section is behind the header currently
            if (rect.top <= HEADER_HEIGHT && rect.bottom > HEADER_HEIGHT) {
                foundSection = section;
                break;
            }
        }

        // Check if we're near the top and should use first section
        if (!foundSection && cachedSections.length > 0) {
            const firstSection = cachedSections[0];
            const rect = firstSection.getBoundingClientRect();
            if (rect.top > 0 && rect.top <= HEADER_HEIGHT + SCROLL_TOLERANCE) {
                foundSection = firstSection;
            }
        }

        // Batch all signal updates to prevent multiple re-renders
        batch(() => {
            if (foundSection) {
                const themeAttr = foundSection.getAttribute('data-section-theme');
                const theme = themeAttr && themeAttr in sectionColorThemeClasses
                    ? themeAttr as ThemeName
                    : null;

                if (theme) {
                    const sectionTheme = sectionColorThemeClasses[theme];
                    setHeaderVariant(sectionTheme.surface || null);
                } else {
                    setHeaderVariant(null);
                }
            } else {
                setHeaderVariant(null);
            }
        });
    };

    const handleScroll = () => {
        if (!ticking) {
            requestAnimationFrame(() => {
                updateCurrentSection();
                ticking = false;
            });
            ticking = true;
        }
    };

    onMount(() => {
        if (isServer) return;

        // Cache sections on mount
        cacheSections();
        updateCurrentSection();

        window.addEventListener('scroll', handleScroll, { passive: true });

        // Re-cache sections when DOM might change (route changes, dynamic content)
        // Only observe main content area to avoid unnecessary re-caching from unrelated DOM changes
        const mainContent = document.getElementById('app') || document.querySelector('main');
        const observer = new MutationObserver((mutations) => {
            const hasRelevantChanges = mutations.some(
                (m) => m.type === 'childList' && m.addedNodes.length > 0
            );
            if (hasRelevantChanges) {
                cacheSections();
            }
        });

        if (mainContent) {
            observer.observe(mainContent, { childList: true, subtree: true });
        }

        onCleanup(() => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        });
    });

    return {
        headerVariant,
    };
}
